/**
 * Pure mapper: domain outputs → payment safety card view (Task 7b).
 * Does not call deriveGrace / getCardState / computeCardObligation itself —
 * the caller passes precomputed results so provenance stays honest.
 */

import type { Cents } from '../../domain/money';
import type { GraceResult, GraceStatus } from '../../domain/grace';
import type { CardObligation } from '../../domain/obligations';
import type { AutopayView, PaymentSafetyCardView, PostedPaymentView, SafetyField } from './types';

export type BuildSafetyCardInput = {
  card_id: string;
  label: string;
  /** Observed statement balance from financial observation (or null if never observed). */
  statement_balance_cents: Cents | null;
  statement_balance_observed_at: string | null;
  payment_due_date: string | null;
  payment_due_date_observed_at: string | null;
  minimum_payment_cents: Cents | null;
  minimum_payment_observed_at: string | null;
  autopay: {
    enabled: boolean | null;
    kind: string;
    amount_cents: Cents | null;
    observed_at: string | null;
  };
  obligation: CardObligation;
  grace: GraceResult;
  cash_reserved_cents: Cents;
  last_posted_payment: {
    amount_cents: Cents;
    posted_date: string;
    observed_at: string;
  } | null;
};

function observed<T>(value: T, observed_at: string): SafetyField<T> {
  return { value, provenance: 'observed', observed_at };
}

function computed<T>(value: T): SafetyField<T> {
  return { value, provenance: 'computed' };
}

/**
 * Build one card row. Missing observations stay null — never invent balances or dates.
 * spend_allowed is true only when grace.status === 'active'.
 */
export function buildPaymentSafetyCard(input: BuildSafetyCardInput): PaymentSafetyCardView {
  const { grace, obligation, autopay } = input;

  const statementBalance: SafetyField<Cents | null> =
    input.statement_balance_observed_at !== null
      ? observed(input.statement_balance_cents, input.statement_balance_observed_at)
      : computed(null);

  const dueDate: SafetyField<string | null> =
    input.payment_due_date_observed_at !== null
      ? observed(input.payment_due_date, input.payment_due_date_observed_at)
      : computed(null);

  const minimumDue: SafetyField<Cents | null> =
    input.minimum_payment_observed_at !== null
      ? observed(input.minimum_payment_cents, input.minimum_payment_observed_at)
      : computed(obligation.minimum_due_cents);

  const autopayView: AutopayView = {
    enabled: autopay.enabled,
    kind: autopay.kind,
    amount_cents: autopay.amount_cents,
  };
  const autopayField: SafetyField<AutopayView> =
    autopay.observed_at !== null
      ? observed(autopayView, autopay.observed_at)
      : computed({ enabled: null, kind: 'unknown', amount_cents: null });

  const lastPayment: SafetyField<PostedPaymentView | null> =
    input.last_posted_payment !== null
      ? observed(
          {
            amount_cents: input.last_posted_payment.amount_cents,
            posted_date: input.last_posted_payment.posted_date,
          },
          input.last_posted_payment.observed_at,
        )
      : computed(null);

  const status: GraceStatus = grace.status;

  return {
    card_id: input.card_id,
    label: input.label,
    statement_balance: statementBalance,
    payment_due_date: dueDate,
    minimum_due: minimumDue,
    autopay: autopayField,
    due_now: computed(obligation.due_now_cents),
    next_due_date: computed(obligation.next_due_date),
    cash_reserved: computed(input.cash_reserved_cents),
    grace_status: computed(status),
    grace_reason: computed(grace.reason),
    spend_allowed: status === 'active',
    last_payment_posted: lastPayment,
  };
}
